import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Captcha from "@/components/Captcha";

export default function Register() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [captchaToken, setCaptchaToken] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 chars"); return; }
    if (!captchaToken) { toast.error("Please complete the captcha"); return; }
    setBusy(true);
    try {
      await api.post("/auth/register", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        captcha_token: captchaToken,
      });
      toast.success("Account created!");
      await refresh();
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
      setCaptchaToken("");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="register-page">
      <div className="w-full max-w-md glass-strong rounded-3xl p-8 sm:p-10">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-black tracking-tight">Join SavageBoy</h1>
          <p className="text-zinc-400 text-sm mt-2">Create your account and start liking.</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="reg-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Username"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="reg-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="reg-password"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Password (min 6 chars)"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none"
            />
          </div>
          <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken("")} />
          <button
            data-testid="reg-submit"
            type="submit"
            disabled={busy || !captchaToken}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E50914] hover:bg-[#F6121D] text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="w-4 h-4" /> {busy ? "Creating..." : "Create Account"}
          </button>
        </form>
        <p className="text-sm text-zinc-400 text-center mt-6">
          Already have an account?{" "}
          <Link to="/login" data-testid="goto-login" className="text-[#E50914] font-semibold hover:underline">Login</Link>
        </p>
      </div>
    </div>
  );
}
